import styled, { css, keyframes } from "styled-components";

// Entrance played once when the hero mounts, i.e. right after the page's data
// has replaced the loading skeleton. Skipped entirely with reduced motion.
const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-32px); }
  to { opacity: 1; transform: none; }
`;

const wipe = keyframes`
  from { opacity: 0.4; clip-path: inset(0 100% 0 0); }
  to { opacity: 1; clip-path: inset(0 0 0 0); }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(6px); }
`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: none; }
`;

const entrance = (name, duration, delay) => css`
  animation: ${name} ${duration} cubic-bezier(0.2, 0.7, 0.2, 1) ${delay} both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const HeroWrapper = styled.div`
  display: flex;
  width: 100%;
  position: relative;
`;

export const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: flex-end;
  margin-top: 140px;
  &.right {
    text-align: right;
  }
  z-index: 1;
  @media (max-width: 768px) {
    margin-top: 33px;
    margin-bottom: 33px;
  }
  /* Full-screen homepage hero: centred in the blue band. */
  .header--home & {
    @media (min-width: 769px) {
      margin-top: 0;
      justify-content: center;
      padding-bottom: 40px;
    }
  }
`;

export const Title = styled.h1`
  ${entrance(slideIn, "0.9s", "0.1s")}
  max-width: 400px;
  margin-right: -213px;
  padding-right: 40px;
  margin-bottom: 20px;
  text-transform: uppercase;
  @media (max-width: 768px) {
    max-width: 115px;
    margin-right: -28px;
    padding-right: 0px;
    margin-bottom: 5px;
  }
`;

export const BigSubtitle = styled.h1`
  ${entrance(slideIn, "0.9s", "0.3s")}
  color: #fafbff;
  font-size: 6.7rem;
  font-weight: bold;
  margin-right: -213px;
  @media (max-width: 768px) {
    font-size: 1.81rem;
    margin-right: -28px;
  }
`;

export const ImageContainer = styled.div`
  min-width: 1000px;
  min-height: 450px;
  position: relative;
  margin-bottom: -90px;
  @media (max-width: 768px) {
    width: 70%;
    min-width: 70%;
    min-height: auto;
    margin-bottom: -27px;
  }
  /* Full-screen homepage hero: the column runs to the bottom of the screen
     and widens with it, the image covering it. */
  .header--home & {
    @media (min-width: 769px) {
      display: flex;
      width: max(1000px, 52vw);
    }
  }
`;

export const Image = styled.img`
  ${entrance(wipe, "1.2s", "0s")}
  width: 1000px;
  @media (max-width: 768px) {
    width: 100%;
  }
  /* Out of the flow, so its natural 560px height cannot prop the hero open
     on a short screen; the column's own size decides. */
  .header--home & {
    @media (min-width: 769px) {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

// "Découvrir ↓" at the foot of the full-screen hero: bobs three times, then
// rests; a click scrolls to the content.
export const ScrollCue = styled.button`
  ${entrance(riseIn, "0.6s", "1.2s")}
  position: absolute;
  z-index: 2; /* above the title column, which stretches over it */
  left: 10%;
  bottom: 32px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  border: 0;
  background: none;
  color: #fafbff;
  font: inherit;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.25s ease;
  svg {
    width: 22px;
    height: 22px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    animation: ${bob} 1.4s ease-in-out 2s 3;
  }
  &:hover,
  &:focus-visible {
    opacity: 1;
  }
  &:focus-visible {
    outline: 3px solid #02c5ff;
    outline-offset: 4px;
  }
  @media (max-width: 768px) {
    display: none;
  }
  @media (prefers-reduced-motion: reduce) {
    svg {
      animation: none;
    }
  }
`;

export const VideoBtnContainer = styled.div`
  ${entrance(riseIn, "0.6s", "0.9s")}
  display: flex;
  align-items: center;
  padding-left: 100px;
  position: absolute;
  bottom: 20px;
  @media (max-width: 768px) {
    padding-left: 10px;
  }
`;

export const VideoLink = styled.a`
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  cursor: pointer;
  &:focus-visible {
    outline: 3px solid #02c5ff;
    outline-offset: 4px;
    border-radius: 100px;
  }
`;

export const PlayBtn = styled.div`
  height: 50px;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0043ff;
  border: 2px solid #fafbff;
  border-radius: 100px;
  transition: transform 0.3s ease, background-color 0.3s ease;
  ${VideoLink}:hover > &,
  ${VideoLink}:focus-visible > & {
    transform: scale(1.12);
    background-color: #043dba;
  }
  img {
    margin-left: 3px;
  }
  @media (max-width: 768px) {
    height: 20px;
    width: 20px;
    border: 1px solid #fafbff;
    img {
      margin-left: 1px;
      width: 5px;
    }
  }
`;

export const Label = styled.span`
  margin-left: 10px;
  color: #fafbff;
  font-size: 1.24rem;
  font-weight: 500;
  @media (max-width: 768px) {
    margin-left: 6px;
    font-size: 0.81rem;
  }
`;
